/**
 * The shapes a field can take on the entry form, and how to name the one a
 * field is already in.
 *
 * A **shape** is one component id plus a props preset. Nothing else. Both
 * halves already have a column — `app.field.component` and `app.field.props` —
 * so choosing a shape writes what a field write has always written, and this
 * file adds no state to the database and no migration to the plan.
 *
 * **Why the name is derived and never stored.** A `variant` column would be a
 * third copy of a fact already stored twice, and the two would drift the first
 * time somebody patched `props` without patching the name. A screen would then
 * show a word that no longer described the control beside it, which is the one
 * thing a preview must never do. `shapeOf` reads the truth instead, and answers
 * null — "tuned by hand" — when no shape describes it. The preview always
 * renders from `component` and `props`, so it is right whatever it is called.
 *
 * **Why this is not in `registry/`.** The component registry is a frozen
 * contract: a change to it is a release, and every id in it is a promise to
 * every device already in the field. These presets are product wording and
 * product grouping over that contract. They must be free to move without a
 * registry version, and every id they name is compile-checked against the
 * generated registry anyway, so nothing is lost by keeping them here.
 *
 * **Matching is behavioural, not JSON equality.** Every template field carries
 * tuning — Daily Sales ships its amount with `allow_expression`, its date with
 * `mode` and `default` — and exact equality would call all of them tuned by
 * hand on the first screen a shopkeeper opened. So each value type declares
 * which props it actually differs on, those are compared after normalising an
 * absent prop to the component's own default, and everything else is tuning
 * that never changes what the shape is called.
 */
import { INLINE_OPTION_LIMIT } from './api';
import type { ComponentId } from './generated/component-ids';
import { COMPONENTS, COMPONENTS_FOR_VALUE_TYPE } from './generated/components';
import type { ValueTypeId } from './generated/value-types';

/** One shape: a name for a person, and the two columns a save writes. */
export type FieldShape = {
  /** Stable. It reaches the device in a route param and nowhere else. */
  readonly id: string;
  /** The catalogue key for the shape's name. Two or three words. */
  readonly labelKey: string;
  /** The catalogue key for the one line under it: when to reach for this one. */
  readonly whyKey: string;
  readonly component: ComponentId;
  /** Written to `app.field.props` verbatim when somebody picks this shape. */
  readonly props: Readonly<Record<string, unknown>>;
  /**
   * The most choices this shape can draw the way its name promises. Absent
   * means the shape does not depend on a count.
   *
   * Only a choice field carries one. "Buttons in a row" is four segments, and
   * past four the component falls back to a row that opens a sheet — which is
   * a different shape wearing this one's name. The gallery reads the cap
   * against the field's own choices and greys the card rather than draw a
   * picture the name contradicts. See `shapeHoldsChoices`.
   */
  readonly optionCap?: number;
};

/**
 * How many chips the "Chips" shape keeps on the form before the sheet takes
 * over. Twelve, not unlimited: past a dozen a wrapping pile of chips is a wall
 * and not a choice, and the component's own comment says so. The shape writes
 * this as `max_visible`, and `shapeOf` reads it back.
 */
export const CHIPS_ON_FORM_LIMIT = 12;

type Props = Readonly<Record<string, unknown>>;

/* -------------------------------------------------------------------------- */
/* The table                                                                  */
/* -------------------------------------------------------------------------- */

const MONEY: readonly FieldShape[] = [
  {
    id: 'money_keypad',
    labelKey: 'field.shapes.money_keypad',
    whyKey: 'field.shapes.money_keypad_why',
    component: 'money.calculator',
    props: {},
  },
  {
    id: 'money_quick',
    labelKey: 'field.shapes.money_quick',
    whyKey: 'field.shapes.money_quick_why',
    component: 'money.calculator',
    props: { quick_amounts: ['100', '500', '1000'] },
  },
  {
    id: 'money_sign',
    labelKey: 'field.shapes.money_sign',
    whyKey: 'field.shapes.money_sign_why',
    component: 'money.calculator',
    props: { sign_control: true },
  },
];

/**
 * The one value type with two components behind it, and the only place the
 * choice of shape is also a choice of component.
 */
const NUMBER: readonly FieldShape[] = [
  {
    id: 'number_type',
    labelKey: 'field.shapes.number_type',
    whyKey: 'field.shapes.number_type_why',
    component: 'number.plain',
    props: {},
  },
  {
    id: 'number_stepper',
    labelKey: 'field.shapes.number_stepper',
    whyKey: 'field.shapes.number_stepper_why',
    component: 'number.stepper',
    props: { step: 1 },
  },
  {
    // `unit_list` is the PROP and not the column. The column exists and the
    // structure editor has always written it, but `liveFields` never selects it
    // and `RecordSchemaField` has no room for it, so the entry form has never
    // seen it. The prop is what `number-plain.tsx` actually reads.
    id: 'number_units',
    labelKey: 'field.shapes.number_units',
    whyKey: 'field.shapes.number_units_why',
    component: 'number.plain',
    props: { unit_list: ['kg', 'g', 'pcs'] },
  },
];

const TEXT: readonly FieldShape[] = [
  {
    id: 'text_line',
    labelKey: 'field.shapes.text_line',
    whyKey: 'field.shapes.text_line_why',
    component: 'text.plain',
    props: {},
  },
];

const LONG_TEXT: readonly FieldShape[] = [
  {
    id: 'long_three',
    labelKey: 'field.shapes.long_three',
    whyKey: 'field.shapes.long_three_why',
    component: 'text.multiline',
    props: { rows: 3 },
  },
  {
    // Five and not six. Blade caps a text area at five lines and `rowsFor`
    // clamps to it, so a card promising six would sit above a control drawing
    // five. The matcher clamps the same way, so a field already holding six
    // reads as this shape — which is what it draws.
    id: 'long_five',
    labelKey: 'field.shapes.long_five',
    whyKey: 'field.shapes.long_five_why',
    component: 'text.multiline',
    props: { rows: 5 },
  },
];

/**
 * Two shapes, and deliberately not three.
 *
 * `date.picker` can draw a clock, and a shape cannot reach for it: `mode:
 * 'datetime'` makes the field read back an ISO timestamp, and a `date` field's
 * column takes `YYYY-MM-DD` and refuses anything else. A shape may not change
 * what a field stores. The clock belongs to the `datetime` value type below,
 * which a template can ask for and the add flow cannot.
 */
const DATE: readonly FieldShape[] = [
  {
    id: 'date_empty',
    labelKey: 'field.shapes.date_empty',
    whyKey: 'field.shapes.date_empty_why',
    component: 'date.picker',
    props: {},
  },
  {
    id: 'date_today',
    labelKey: 'field.shapes.date_today',
    whyKey: 'field.shapes.date_today_why',
    component: 'date.picker',
    props: { default: 'today' },
  },
];

const DATETIME: readonly FieldShape[] = [
  {
    id: 'datetime_moment',
    labelKey: 'field.shapes.datetime_moment',
    whyKey: 'field.shapes.datetime_moment_why',
    component: 'date.picker',
    props: { mode: 'datetime', default: 'now' },
  },
];

/**
 * One shape, and deliberately not two.
 *
 * `boolean.switch` takes `on_label_key` and `off_label_key`, and a shape that
 * set them would put a catalogue key on the entry form: `packages/ui` holds no
 * catalogue, `switchLabelFor` hands back the raw string, and the entry form
 * passes props through untranslated. A word beside the switch is worth having,
 * but it has to come from the tenant, through a path that resolves it — not
 * from a preset here.
 */
const BOOLEAN: readonly FieldShape[] = [
  {
    id: 'boolean_switch',
    labelKey: 'field.shapes.boolean_switch',
    whyKey: 'field.shapes.boolean_switch_why',
    component: 'boolean.switch',
    props: {},
  },
];

/**
 * Three shapes of one component, and `multi` is not one of them.
 *
 * A `select` field stores one uuid in a reference slot, and the server refuses
 * anything else. `multi: true` makes the component read back an array, so a
 * field wearing it could never save. That prop belongs to `multi_select`, which
 * has its own row below and is not offered in the add flow.
 *
 * `allow_search` is what turns the segmented control into chips: it is the
 * clause in `prefersSegmentedControl` that fails. It also raises the sheet's
 * filter box, which is why the sheet shape carries it too — without it the
 * shape called "Search list" would open a list with nothing to search with.
 *
 * The chips shape also carries `max_visible`. Without it the component keeps
 * only four chips on the form and puts a fifth choice behind a sheet — the same
 * sheet the buttons fall back to — so a field with five choices drew three
 * identical rows under three different names. `CHIPS_ON_FORM_LIMIT` is what
 * lets the card called "Chips" wrap on the form the way its line promises.
 */
const SELECT: readonly FieldShape[] = [
  {
    id: 'select_buttons',
    labelKey: 'field.shapes.select_buttons',
    whyKey: 'field.shapes.select_buttons_why',
    component: 'select.chips',
    props: {},
    optionCap: INLINE_OPTION_LIMIT,
  },
  {
    id: 'select_chips',
    labelKey: 'field.shapes.select_chips',
    whyKey: 'field.shapes.select_chips_why',
    component: 'select.chips',
    props: { allow_search: true, max_visible: CHIPS_ON_FORM_LIMIT },
    optionCap: CHIPS_ON_FORM_LIMIT,
  },
  {
    id: 'select_sheet',
    labelKey: 'field.shapes.select_sheet',
    whyKey: 'field.shapes.select_sheet_why',
    component: 'select.chips',
    props: { max_visible: 1, allow_search: true },
  },
];

/**
 * One shape. A multi select points at a list, as a reference does (Decision 4
 * of `docs/plans/dependent-choices-and-multi-select.md`), so it has no options
 * of its own for chips to draw: it is a row that opens the list with a tick on
 * each row, and chips under the row for what is ticked.
 */
const MULTI_SELECT: readonly FieldShape[] = [
  {
    id: 'multi_list',
    labelKey: 'field.shapes.multi_list',
    whyKey: 'field.shapes.multi_list_why',
    component: 'reference.multi_picker',
    props: {},
  },
];

/**
 * Add-new first, because a new reference field almost always points at a
 * directory that is still empty. The templates agree: every reference field
 * they ship carries `allow_create`.
 *
 * A bare field is therefore the SECOND shape, not the first. `allow_create`
 * absent means no create row, and the table below says so rather than pretending
 * the first card is the default.
 */
const REFERENCE: readonly FieldShape[] = [
  {
    id: 'reference_create',
    labelKey: 'field.shapes.reference_create',
    whyKey: 'field.shapes.reference_create_why',
    component: 'reference.search_picker',
    props: { allow_create: true },
  },
  {
    id: 'reference_search',
    labelKey: 'field.shapes.reference_search',
    whyKey: 'field.shapes.reference_search_why',
    component: 'reference.search_picker',
    props: {},
  },
];

const ATTACHMENT: readonly FieldShape[] = [
  {
    // Bare, not `{ source: 'any' }`. `addButtonLabel` reads an absent source
    // and 'any' the same way, and an empty preset is what a field created
    // before this table already holds.
    id: 'file_any',
    labelKey: 'field.shapes.file_any',
    whyKey: 'field.shapes.file_any_why',
    component: 'file.picker',
    props: {},
  },
  {
    id: 'file_camera',
    labelKey: 'field.shapes.file_camera',
    whyKey: 'field.shapes.file_camera_why',
    component: 'file.picker',
    props: { source: 'camera' },
  },
  {
    id: 'file_three',
    labelKey: 'field.shapes.file_three',
    whyKey: 'field.shapes.file_three_why',
    component: 'file.picker',
    props: { max_files: 3 },
  },
];

const PHONE: readonly FieldShape[] = [
  {
    id: 'phone_plain',
    labelKey: 'field.shapes.phone_plain',
    whyKey: 'field.shapes.phone_plain_why',
    component: 'text.phone',
    props: {},
  },
  {
    id: 'phone_call',
    labelKey: 'field.shapes.phone_call',
    whyKey: 'field.shapes.phone_call_why',
    component: 'text.phone',
    props: { tap_to_call: true },
  },
];

const LOCATION: readonly FieldShape[] = [
  {
    id: 'location_pin',
    labelKey: 'field.shapes.location_pin',
    whyKey: 'field.shapes.location_pin_why',
    component: 'map.picker',
    props: {},
  },
  {
    id: 'location_manual',
    labelKey: 'field.shapes.location_manual',
    whyKey: 'field.shapes.location_manual_why',
    component: 'map.picker',
    props: { allow_manual: true },
  },
];

/**
 * A lines field draws one way: its lines as a table on the web and as cards on
 * the phone. The form draws it through its own line path, not this component.
 */
const LINES: readonly FieldShape[] = [
  {
    id: 'lines_table',
    labelKey: 'field.shapes.lines_table',
    whyKey: 'field.shapes.lines_table_why',
    component: 'lines.table',
    props: {},
  },
];

const READONLY: readonly FieldShape[] = [
  {
    id: 'readonly_value',
    labelKey: 'field.shapes.readonly_value',
    whyKey: 'field.shapes.readonly_value_why',
    component: 'value.readonly',
    props: {},
  },
];

/**
 * Every value type, and the shapes it can take. Total over the closed 18, so a
 * caller never has to ask whether a type is in here.
 *
 * `quantity` shares `number`'s array by reference: they are the same two
 * components with the same three shapes, and two copies would drift.
 */
export const SHAPES_FOR: { readonly [K in ValueTypeId]: readonly FieldShape[] } = {
  text: TEXT,
  long_text: LONG_TEXT,
  number: NUMBER,
  money: MONEY,
  quantity: NUMBER,
  date: DATE,
  datetime: DATETIME,
  boolean: BOOLEAN,
  select: SELECT,
  multi_select: MULTI_SELECT,
  reference: REFERENCE,
  attachment: ATTACHMENT,
  phone: PHONE,
  location: LOCATION,
  formula: READONLY,
  rollup: READONLY,
  lookup: READONLY,
  lines: LINES,
};

/** The shapes one value type offers, in the order a person should read them. */
export const shapesFor = (type: ValueTypeId): readonly FieldShape[] => SHAPES_FOR[type];

/**
 * Every shape id, once, across the types. A wire that names a shape (the
 * agent's tools, a setup draft) takes one of these, and the type of the field
 * it sits on says whether the shape fits.
 */
export const SHAPE_IDS: readonly string[] = [...new Set(Object.values(SHAPES_FOR).flat().map((shape) => shape.id))];

/** A shape by id, for one type. Null when the type does not offer it. */
export const shapeById = (type: ValueTypeId, id: string): FieldShape | null =>
  SHAPES_FOR[type].find((shape) => shape.id === id) ?? null;

/**
 * Whether a shape can draw this many choices the way its name says.
 *
 * True for every shape without a cap, and for a capped one at or under it. The
 * gallery asks this per card: a "Buttons in a row" card over a field with six
 * choices would render the fallback row — the picture of a different shape —
 * so the card is greyed and says why instead.
 */
export const shapeHoldsChoices = (shape: FieldShape, optionCount: number): boolean =>
  shape.optionCap === undefined || optionCount <= shape.optionCap;

/* -------------------------------------------------------------------------- */
/* Naming the shape a field is already in                                     */
/* -------------------------------------------------------------------------- */

/**
 * Which props decide the shape, per value type.
 *
 * Everything not named here is tuning: a limit, a placeholder, a default
 * country. Tuning never renames a shape, which is what lets a template's own
 * fields carry their settings and still read as "Keypad" and "Starts on today"
 * rather than as twelve fields tuned by hand.
 */
const DISCRIMINANTS: { readonly [K in ValueTypeId]: readonly string[] } = {
  text: [],
  long_text: ['rows'],
  number: ['unit_list'],
  money: ['sign_control', 'quick_amounts'],
  quantity: ['unit_list'],
  date: ['mode', 'default'],
  datetime: ['mode'],
  boolean: ['on_label_key', 'off_label_key'],
  select: ['multi', 'allow_search', 'max_visible'],
  multi_select: [],
  reference: ['allow_create'],
  attachment: ['source', 'max_files'],
  phone: ['tap_to_call'],
  location: ['allow_manual'],
  formula: [],
  rollup: [],
  lookup: [],
  lines: [],
};

const isList = (value: unknown): value is readonly unknown[] =>
  Array.isArray(value) && value.length > 0;

/**
 * The rows a text area draws, clamped the way the component clamps.
 *
 * A mirror of `rowsFor` in `packages/ui/src/fields/text-multiline.logic.ts`,
 * and it has to be a mirror: this package may not import that one, and Blade's
 * five-line ceiling is the reason the table stops at five. Change one and
 * change the other.
 */
const rowsDrawn = (asked: unknown): number => {
  if (typeof asked !== 'number' || !Number.isFinite(asked)) return 3;
  return Math.min(5, Math.max(1, Math.trunc(asked)));
};

const byId = (shapes: readonly FieldShape[], id: string): FieldShape | null =>
  shapes.find((shape) => shape.id === id) ?? null;

/**
 * The shape a stored field is in, or null when no shape describes it.
 *
 * Null is not a failure. It is the honest answer for a field somebody set up
 * prop by prop, and the screens say "tuned by hand" and draw the control
 * anyway. Returning the nearest shape instead would put a name on a control
 * that does not match it.
 */
export const shapeOf = (
  type: ValueTypeId,
  component: ComponentId,
  props: Props,
): FieldShape | null => {
  const shapes = SHAPES_FOR[type];
  // A component this type cannot draw is not a shape of it at any props.
  if (!COMPONENTS_FOR_VALUE_TYPE[type].includes(component)) return null;

  switch (type) {
    case 'text':
    case 'formula':
    case 'rollup':
    case 'lookup':
    case 'lines':
      return shapes[0] ?? null;

    case 'long_text': {
      const rows = rowsDrawn(props.rows);
      if (rows === 3) return byId(shapes, 'long_three');
      if (rows === 5) return byId(shapes, 'long_five');
      return null;
    }

    case 'money': {
      const signed = props.sign_control === true;
      const quick = isList(props.quick_amounts);
      if (signed && quick) return null;
      if (signed) return byId(shapes, 'money_sign');
      if (quick) return byId(shapes, 'money_quick');
      return byId(shapes, 'money_keypad');
    }

    case 'number':
    case 'quantity': {
      // The component settles it first: a stepper is a stepper whatever its
      // step, and it has no shape with units.
      if (component === 'number.stepper') return byId(shapes, 'number_stepper');
      return isList(props.unit_list)
        ? byId(shapes, 'number_units')
        : byId(shapes, 'number_type');
    }

    case 'date': {
      // A date field drawing a clock is a field that cannot save. Nothing here
      // names it, so it reads as tuned by hand and the screen says so.
      if (props.mode !== undefined && props.mode !== 'date') return null;
      if (props.default === undefined || props.default === 'none') {
        return byId(shapes, 'date_empty');
      }
      return props.default === 'today' ? byId(shapes, 'date_today') : null;
    }

    case 'datetime':
      return props.mode === 'datetime' ? byId(shapes, 'datetime_moment') : null;

    case 'boolean':
      return props.on_label_key === undefined && props.off_label_key === undefined
        ? byId(shapes, 'boolean_switch')
        : null;

    case 'select': {
      // `multi` on a single select is a field that cannot save. Named as tuned
      // by hand rather than silently blessed.
      if (props.multi === true) return null;
      if (props.max_visible === 1) return byId(shapes, 'select_sheet');
      if (props.allow_search === true) {
        // The chips shape at its own limit, or a field from before the limit
        // was written: both draw chips on the form, so both read as chips.
        return props.max_visible === undefined || props.max_visible === CHIPS_ON_FORM_LIMIT
          ? byId(shapes, 'select_chips')
          : null;
      }
      return props.max_visible === undefined ? byId(shapes, 'select_buttons') : null;
    }

    case 'multi_select':
      return byId(shapes, 'multi_list');

    case 'reference':
      return props.allow_create === true
        ? byId(shapes, 'reference_create')
        : byId(shapes, 'reference_search');

    case 'attachment': {
      const anySource = props.source === undefined || props.source === 'any';
      const capped = props.max_files !== undefined;
      if (props.source === 'camera') return capped ? null : byId(shapes, 'file_camera');
      if (!anySource) return null;
      if (!capped) return byId(shapes, 'file_any');
      return props.max_files === 3 ? byId(shapes, 'file_three') : null;
    }

    case 'phone':
      return props.tap_to_call === true
        ? byId(shapes, 'phone_call')
        : byId(shapes, 'phone_plain');

    case 'location':
      return props.allow_manual === true
        ? byId(shapes, 'location_manual')
        : byId(shapes, 'location_pin');
  }
};

/**
 * The props a re-shape writes: the shape's own, over whatever tuning survives.
 *
 * A person who set a minimum, a placeholder or a hint on a field did not ask
 * for it to be thrown away when they change how the field looks. So the held
 * props are carried across, minus this type's discriminants — those are exactly
 * what the new shape is choosing — and minus anything the target component does
 * not declare, which would be refused by the server anyway.
 *
 * The two list discriminants are the exception inside the exception. A shop
 * that typed its own units or its own quick amounts keeps them: the shape is
 * asking for a list to exist, not for these particular words.
 */
export const propsForShape = (
  type: ValueTypeId,
  shape: FieldShape,
  held: Props,
): Record<string, unknown> => {
  const declared = COMPONENTS[shape.component].props;
  const discriminants = DISCRIMINANTS[type];
  const next: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(held)) {
    if (declared[key] === undefined) continue;
    if (discriminants.includes(key)) continue;
    next[key] = value;
  }

  for (const [key, value] of Object.entries(shape.props)) {
    // A list the shop already wrote beats the list this table ships.
    const wasList = isList(held[key]);
    next[key] = Array.isArray(value) && wasList ? held[key] : value;
  }

  return next;
};

/* -------------------------------------------------------------------------- */
/* A shape asked for in words, not tapped                                     */
/* -------------------------------------------------------------------------- */

/**
 * How a shape is asked for on a wire: by id, and with the two lists a person
 * spells out rather than accepts. The agent's field tools and the setup draft
 * both carry exactly this, so a shape said to the assistant and a shape tapped
 * in the gallery write the same two columns.
 */
export type ShapeAsk = {
  readonly shape?: string;
  /** For a number or quantity: the units beside the box. Implies `number_units`. */
  readonly units?: readonly string[];
  /** For money: the amounts one tap enters. Implies `money_quick`. */
  readonly quickAmounts?: readonly string[];
};

export type ShapeWrite =
  | { readonly ok: true; readonly shape: FieldShape; readonly component: ComponentId; readonly props: Record<string, unknown> }
  | { readonly ok: false; readonly message: string };

const hasWords = (list: readonly string[] | undefined): list is readonly string[] => list !== undefined && list.length > 0;

/**
 * The two columns an ask writes, or why it cannot.
 *
 * Null when the ask says nothing about the shape: the caller keeps whatever
 * default it had, which is the composed one for the assistant and the first
 * card for the gallery. A list without a shape implies the one shape that
 * draws it; a list with a shape that cannot draw it is refused rather than
 * dropped, because a person who typed their units did not ask for them to
 * vanish. `held` is the field's props today, carried across the way a
 * re-shape in the gallery carries them.
 */
export const resolveShape = (type: ValueTypeId, ask: ShapeAsk, held: Props = {}): ShapeWrite | null => {
  const units = hasWords(ask.units) ? ask.units : undefined;
  const quick = hasWords(ask.quickAmounts) ? ask.quickAmounts : undefined;
  const offered = SHAPES_FOR[type].map((shape) => shape.id).join(', ');

  let id = ask.shape;
  if (id === undefined || id === '') {
    if (units === undefined && quick === undefined) return null;
    id = units !== undefined ? 'number_units' : 'money_quick';
  }
  const shape = shapeById(type, id);
  if (shape === null) {
    return {
      ok: false,
      message: `"${id}" is not a shape a ${type} field can take. Its shapes are: ${offered}.`,
    };
  }
  const declared = COMPONENTS[shape.component].props;
  if (units !== undefined && declared.unit_list === undefined) {
    return { ok: false, message: `Units go with the number_units shape on a number or quantity field, not with ${shape.id} on a ${type} field.` };
  }
  if (quick !== undefined && declared.quick_amounts === undefined) {
    return { ok: false, message: `Quick amounts go with the money_quick shape on a money field, not with ${shape.id} on a ${type} field.` };
  }

  const props = propsForShape(type, shape, held);
  if (units !== undefined) props.unit_list = [...units];
  if (quick !== undefined) props.quick_amounts = [...quick];
  return { ok: true, shape, component: shape.component, props };
};
